import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Properties from './pages/Properties';
import PropertyDetail from './pages/PropertyDetail';
import DealAnalyzer from './pages/DealAnalyzer';
import Vendors from './pages/Vendors';
import Approvals from './pages/Approvals';
import Accounting from './pages/Accounting';
import Drawings from './pages/Drawings';
import Sales from './pages/Sales';
import Documents from './pages/Documents';
import AuditPage from './pages/AuditPage';
import BOQCompare from './pages/BOQCompare';
import Tasks from './pages/Tasks';
import Team from './pages/Team';
import CashFlow from './pages/CashFlow';
import Retention from './pages/Retention';
import DemoTour from './pages/DemoTour';
import VendorPortal from './pages/VendorPortal';
import AdminSettings from './pages/AdminSettings';
import AdminSyncRuns from './pages/AdminSyncRuns';
import TransactionsBrowser from './pages/TransactionsBrowser';
import PresentPlatform from './pages/PresentPlatform';
import Auctions from './pages/Auctions';
import Trends from './pages/Trends';
import LiveListings from './pages/LiveListings';
import RentalLeaderboardPanel from './pages/RentalLeaderboardPanel';
import ExplainerPage from './pages/ExplainerPage';
import MyHome from './pages/MyHome';
import Help from './pages/Help';
import DailyDigest from './pages/DailyDigest';
import ChangePassword from './pages/ChangePassword';
import TwoFactorChallenge from './pages/TwoFactorChallenge';
import TwoFactorSetup from './pages/TwoFactorSetup';
import MobileSnag from './pages/MobileSnag';
import MobileMore from './pages/MobileMore';
import MobileHome from './pages/mobile/MobileHome';
import MobileProperties from './pages/mobile/MobileProperties';
import MobilePropertyDetail from './pages/mobile/MobilePropertyDetail';
import MobileTasks from './pages/mobile/MobileTasks';
import MobileTaskDetail from './pages/mobile/MobileTaskDetail';
import MobileDealAnalyzer from './pages/mobile/MobileDealAnalyzer';
import MobileAuctions from './pages/mobile/MobileAuctions';
import MobileAuctionDetail from './pages/mobile/MobileAuctionDetail';
import MobileTrends from './pages/mobile/MobileTrends';
import { useIsMobile } from './lib/useIsMobile';

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center"><div className="ambient-bg"/><div className="glass p-6">Loading…</div></div>;
  if (!user) return <Navigate to="/" replace />;
  // Force first-login password change (unless already on that page)
  if (user.must_change_password && location.pathname !== '/app/change-password') {
    return <Navigate to="/app/change-password" replace />;
  }
  // Force 2FA setup (workspace-enforced) BEFORE first use
  if (user.require_2fa_setup && !['/app/security', '/app/change-password'].includes(location.pathname)) {
    return <Navigate to="/app/security" replace />;
  }
  // Force 2FA challenge if user has 2FA enabled but hasn't passed for this session
  if (user.require_2fa && !['/app/2fa-challenge', '/app/change-password'].includes(location.pathname)) {
    return <Navigate to="/app/2fa-challenge" replace />;
  }
  return children;
}

function AppShell() {
  const location = useLocation();
  // Detect OAuth callback from URL hash SYNCHRONOUSLY (not in useEffect)
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/vendor-portal" element={<VendorPortal />} />
      <Route path="/app/change-password" element={<Protected><ChangePassword /></Protected>} />
      <Route path="/app/2fa-challenge" element={<Protected><TwoFactorChallenge /></Protected>} />
      <Route path="/m/*" element={<Protected><MobileRoutes /></Protected>} />
      <Route path="/app/*" element={<Protected><DesktopOrRedirect /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// When on a small viewport, most /app/* routes get redirected to their mobile counterparts.
// Some routes (mobile-snag, mobile-more, demo-tour) stay on /app/ intentionally.
const MOBILE_MAP = {
  dashboard:       '/m/home',
  properties:      '/m/properties',
  'deal-analyzer': '/m/deal-analyzer',
  tasks:           '/m/tasks',
  auctions:        '/m/auctions',
  trends:          '/m/trends',
};

function DesktopOrRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (isMobile) {
    const seg = location.pathname.replace(/^\/app\//, '').split('/')[0];
    // Special mobile property detail deep-link
    if (seg === 'properties' && location.pathname.match(/^\/app\/properties\/[^/]+$/)) {
      const id = location.pathname.split('/').pop();
      return <Navigate to={`/m/properties/${id}`} replace/>;
    }
    if (MOBILE_MAP[seg]) return <Navigate to={MOBILE_MAP[seg]} replace/>;
  }
  return <AppRoutes />;
}

function MobileRoutes() {
  return (
    <Routes>
      <Route path="home"                   element={<MobileHome />} />
      <Route path="properties"             element={<MobileProperties />} />
      <Route path="properties/:id"         element={<MobilePropertyDetail />} />
      <Route path="tasks"                  element={<MobileTasks />} />
      <Route path="tasks/:id"              element={<MobileTaskDetail />} />
      <Route path="deal-analyzer"          element={<MobileDealAnalyzer />} />
      <Route path="auctions"               element={<MobileAuctions />} />
      <Route path="auctions/:externalId"   element={<MobileAuctionDetail />} />
      <Route path="trends"                 element={<MobileTrends />} />
      <Route path="*"                      element={<Navigate to="home" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="me" element={<MyHome />} />
        <Route path="auctions" element={<Auctions />} />
        <Route path="trends" element={<Trends />} />
        <Route path="market" element={<Trends />} />
        <Route path="market/live" element={<LiveListings />} />
        <Route path="market/transactions" element={<TransactionsBrowser />} />
        <Route path="market/leaderboard" element={<RentalLeaderboardPanel />} />
        <Route path="explain/:source/:sourceId" element={<ExplainerPage />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="properties/:id/boq-compare" element={<BOQCompare />} />
        <Route path="deal-analyzer" element={<DealAnalyzer />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="drawings" element={<Drawings />} />
        <Route path="sales" element={<Sales />} />
        <Route path="documents" element={<Documents />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="team" element={<Team />} />
        <Route path="cash-flow" element={<CashFlow />} />
        <Route path="retention" element={<Retention />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="admin" element={<AdminSettings />} />
        <Route path="admin/sync-runs" element={<AdminSyncRuns />} />
        <Route path="security" element={<TwoFactorSetup />} />
        <Route path="help" element={<Help />} />
        <Route path="help/:slug" element={<Help />} />
        <Route path="daily-digest" element={<DailyDigest />} />
        <Route path="present" element={<PresentPlatform />} />
        <Route path="mobile-snag" element={<MobileSnag />} />
        <Route path="mobile-more" element={<MobileMore />} />
        <Route path="demo-tour" element={<DemoTour />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
